import { motion } from "framer-motion";
import { ErrorBoundary } from "react-error-boundary";
import { ReactNode } from "react";
import { STAGE } from "@/utils/constant/globalEnv.constant";

interface FallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

export const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.5 }}
      role="alert"
      className="h-screen flex justify-center items-center"
    >
      <motion.div
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2 }}
        className=" rounded-2xl p-8 max-w-md w-11/12 text-center bg-white flex items-center justify-center flex-col shadow-lg"
      >
        <motion.img whileHover={{ scale: 1.05 }} src="/icons/error-state.svg" className=""></motion.img>

        <motion.h2 initial={{ x: -20 }} animate={{ x: 0 }} transition={{ delay: 0.3 }} className="text-2xl font-bold text-gray-800 my-4">
          Not you, It’s us
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="bg-gray-50 p-4 rounded-lg text-13 text-text-error mb-6 overflow-auto"
        >
          {["dev", "local"].includes(STAGE) ? error?.message : "Oops! Something went wrong. Please try reloading the page."}
        </motion.p>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          transition={{ type: "spring", stiffness: 400, damping: 17 }}
          onClick={resetErrorBoundary}
          className="text-text-primary underline-offset-4 hover:underline text-13 "
        >
          Reload
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

interface DefaultErrorBoundaryProps {
  children: ReactNode;
}

export const DefaultErrorBoundary = ({ children }: DefaultErrorBoundaryProps) => {
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        window.location.reload();
      }}
    >
      {children}
    </ErrorBoundary>
  );
};
